import React, { useState, useEffect, useRef } from 'react';
import {
  Check,
  X,
  Warning,
  ToggleLeft,
  ToggleRight,
} from '@phosphor-icons/react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { TaxiRouteProperties, RwyConfig, Movement, AircraftCat, AmsZoneProperties } from '../../types/conops';
import { conopsService } from '../../services/conops';

// ─── Token validation badge ────────────────────────────────────────────────────

const TokenBadge: React.FC<{ token: string; valid: boolean | null }> = ({ token, valid }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-bold border ${
      valid === true
        ? 'bg-green-500/10 border-green-500/40 text-green-400'
        : valid === false
        ? 'bg-red-500/10 border-red-500/40 text-red-400'
        : 'bg-[var(--surface-inset)] border-[var(--border)] text-[var(--text-secondary)]'
    }`}
  >
    {valid === true && <Check size={10} weight="bold" />}
    {valid === false && <X size={10} weight="bold" />}
    {token}
  </span>
);

// ─── Toggle group helper ───────────────────────────────────────────────────────

function ToggleGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  activeColor = '#F26430',
}: {
  label: string;
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
  activeColor?: string;
}) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">
        {label}
      </label>
      <div className="flex gap-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`flex-1 py-1.5 text-xs font-bold rounded border transition-all ${
              value === opt.value
                ? 'text-white border-transparent'
                : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-secondary)]'
            }`}
            style={
              value === opt.value
                ? { backgroundColor: activeColor, borderColor: activeColor }
                : {}
            }
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const inputClass =
  'w-full px-2.5 py-1.5 rounded-md border border-[var(--border)] bg-[var(--surface-inset)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[#F26430]';

// ─── Main component ────────────────────────────────────────────────────────────

interface TaxiRouteToolProps {
  icaoCode: string;
  topologyTaxiways: string[];
  existingZones: AmsZoneProperties[];
  map: MapboxMap | null;
  drawRef: React.MutableRefObject<any>;
  onRouteReady: (sequence: string[]) => void;
  onMetadataChange: (meta: Partial<TaxiRouteProperties>) => void;
  isDark: boolean;
}

const PREVIEW_SOURCE = 'taxi-route-preview-text';
const PREVIEW_LAYER = 'taxi-route-preview-text-line';

const TaxiRouteTool: React.FC<TaxiRouteToolProps> = ({
  icaoCode,
  topologyTaxiways,
  existingZones,
  map,
  drawRef,
  onRouteReady,
  onMetadataChange,
  isDark,
}) => {
  const [tab, setTab] = useState<'pintar' | 'escribir'>('escribir');
  const [routeText, setRouteText] = useState('');
  const [tokens, setTokens] = useState<{ token: string; valid: boolean | null }[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [currentToken, setCurrentToken] = useState('');
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    errors: string[];
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Metadata
  const [meta, setMeta] = useState<Partial<TaxiRouteProperties>>({
    id: '',
    name: '',
    rwy_config: 'SOUTH',
    runway: '',
    movement: 'ARR',
    zone_id: '',
    lvp_mandatory: false,
    cat_max: 'C',
    sequence: [],
  });

  const tabBtnClass = (active: boolean) =>
    `flex-1 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors ${
      active
        ? 'border-[#F26430] text-[#F26430]'
        : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
    }`;

  // Parse route text into tokens
  const parseRouteText = (text: string) => {
    const parts = text
      .split(/[>,]/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const validated = parts.map((t) => ({
      token: t,
      valid: topologyTaxiways.length > 0
        ? topologyTaxiways.some((tw) => tw.toUpperCase() === t.toUpperCase())
        : null,
    }));
    setTokens(validated);
    return parts;
  };

  // Handle text input change
  const handleTextChange = (val: string) => {
    setRouteText(val);
    setValidationResult(null);

    const parts = val.split(/[>,]/);
    const last = parts[parts.length - 1].trim();
    setCurrentToken(last);

    if (last.length > 0) {
      setSuggestions(
        topologyTaxiways
          .filter((t) => t.toUpperCase().startsWith(last.toUpperCase()))
          .slice(0, 8)
      );
    } else {
      setSuggestions([]);
    }

    const parsed = parseRouteText(val);
    setMeta((prev) => ({ ...prev, sequence: parsed }));
    onMetadataChange({ ...meta, sequence: parsed });

    // Update map preview
    updateMapPreview(parsed);
  };

  const updateMapPreview = (_sequence: string[]) => {
    // Preview is decorative only — real geometry comes from draw.create
    // We can't reconstruct LineString coords from TWY IDs client-side without topology graph
    // So we just skip coords rendering here; the draw tab handles actual geometry
    if (!map) return;
    // Remove previous preview layer/source if exists
    if (map.getLayer(PREVIEW_LAYER)) map.removeLayer(PREVIEW_LAYER);
    if (map.getSource(PREVIEW_SOURCE)) map.removeSource(PREVIEW_SOURCE);
  };

  const handleValidate = async () => {
    const sequence = tokens.map((t) => t.token).filter((t) => t.length > 0);
    if (sequence.length === 0) return;
    setIsValidating(true);
    try {
      const result = await conopsService.validateRoute(icaoCode, sequence);
      setValidationResult(result);
      if (result.valid) {
        onRouteReady(sequence);
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleMetaChange = (patch: Partial<TaxiRouteProperties>) => {
    const next = { ...meta, ...patch };
    setMeta(next);
    onMetadataChange(next);
  };

  const handleTabPaint = () => {
    setTab('pintar');
    if (drawRef.current && map) {
      try {
        drawRef.current.changeMode('draw_line_string');
      } catch (_) {}
    }
  };

  const handleTabWrite = () => {
    setTab('escribir');
    if (drawRef.current && map) {
      try {
        drawRef.current.changeMode('simple_select');
      } catch (_) {}
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex border-b border-[var(--border)] mb-3">
        <button className={tabBtnClass(tab === 'pintar')} onClick={handleTabPaint}>
          Pintar
        </button>
        <button className={tabBtnClass(tab === 'escribir')} onClick={handleTabWrite}>
          Escribir
        </button>
      </div>

      {/* Tab content */}
      {tab === 'pintar' && (
        <div className="mb-4">
          <p className="text-xs text-[var(--text-secondary)] mb-2">
            Haz clic en el mapa para trazar la ruta punto a punto. La linea se cerrara automaticamente al hacer doble clic.
          </p>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#F26430]/10 border border-[#F26430]/30">
            <span className="w-2 h-2 rounded-full bg-[#F26430] animate-pulse" />
            <span className="text-xs text-[#F26430] font-semibold">Modo dibujo activo</span>
          </div>
        </div>
      )}

      {tab === 'escribir' && (
        <div className="mb-4">
          <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">
            Secuencia de rodaje
          </label>
          <div className="relative">
            <input
              className={inputClass + ' font-mono'}
              placeholder="S4 > M > S2 > J > G"
              value={routeText}
              onChange={(e) => handleTextChange(e.target.value)}
            />
            {suggestions.length > 0 && (
              <div className="absolute z-20 w-full mt-1 rounded-md border border-[var(--border)] bg-[var(--surface)] shadow-lg max-h-40 overflow-y-auto">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    className="w-full text-left px-3 py-1.5 text-xs font-mono hover:bg-[var(--surface-raised)] text-[var(--text-primary)]"
                    onClick={() => {
                      // Replace last token with selected suggestion
                      const parts = routeText.split(/[>,]/);
                      parts[parts.length - 1] = ' ' + s;
                      const newText = parts.join(' > ');
                      handleTextChange(newText);
                      setSuggestions([]);
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Token visualization */}
          {tokens.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tokens.map((t, i) => (
                <TokenBadge key={i} token={t.token} valid={t.valid} />
              ))}
            </div>
          )}

          {/* Validation result */}
          {validationResult && (
            <div
              className={`mt-2 px-3 py-2 rounded-md text-xs font-medium ${
                validationResult.valid
                  ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                  : 'bg-red-500/10 border border-red-500/30 text-red-400'
              }`}
            >
              {validationResult.valid ? (
                <span className="flex items-center gap-1">
                  <Check size={12} weight="bold" /> Ruta valida
                </span>
              ) : (
                <div>
                  <span className="flex items-center gap-1 mb-1">
                    <Warning size={12} weight="bold" /> Ruta invalida
                  </span>
                  <ul className="list-disc list-inside space-y-0.5">
                    {validationResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleValidate}
            disabled={isValidating || tokens.length === 0}
            className="mt-2 w-full py-1.5 text-xs font-bold rounded-md bg-[#F26430] hover:bg-[#d9542a] text-white disabled:opacity-50 transition-colors"
          >
            {isValidating ? 'Validando...' : 'Validar ruta'}
          </button>
        </div>
      )}

      {/* Metadata form — shown in both tabs */}
      <div className="border-t border-[var(--border)] pt-3 flex-1 overflow-y-auto">
        <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3">
          Metadatos de la ruta
        </p>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">ID</label>
          <input
            className={inputClass}
            placeholder="T2-ARR-21L-A"
            value={meta.id || ''}
            onChange={(e) => handleMetaChange({ id: e.target.value })}
          />
        </div>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">Nombre</label>
          <input
            className={inputClass}
            placeholder="T2 Llegada 21L — Estandar"
            value={meta.name || ''}
            onChange={(e) => handleMetaChange({ name: e.target.value })}
          />
        </div>

        <ToggleGroup<RwyConfig>
          label="Config. pista"
          options={[
            { label: 'SUR', value: 'SOUTH' },
            { label: 'NORTE', value: 'NORTH' },
            { label: 'ANY', value: 'ANY' },
          ]}
          value={meta.rwy_config || 'SOUTH'}
          onChange={(v) => handleMetaChange({ rwy_config: v })}
        />

        <div className="mb-3">
          <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">Pista</label>
          <input
            className={inputClass}
            placeholder="21L"
            value={meta.runway || ''}
            onChange={(e) => handleMetaChange({ runway: e.target.value })}
          />
        </div>

        <ToggleGroup<Movement>
          label="Movimiento"
          options={[
            { label: 'ARR', value: 'ARR' },
            { label: 'DEP', value: 'DEP' },
          ]}
          value={meta.movement || 'ARR'}
          onChange={(v) => handleMetaChange({ movement: v })}
          activeColor={meta.movement === 'ARR' ? '#22c55e' : '#F26430'}
        />

        <div className="mb-3">
          <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">Zona AMS</label>
          <select
            className={inputClass}
            value={meta.zone_id || ''}
            onChange={(e) => handleMetaChange({ zone_id: e.target.value })}
          >
            <option value="">-- Seleccionar --</option>
            {existingZones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.id} — {z.name}
              </option>
            ))}
          </select>
        </div>

        {/* LVP toggle — amber highlight when ON */}
        <div className="mb-3">
          <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">
            LVP obligatoria
          </label>
          <button
            onClick={() => handleMetaChange({ lvp_mandatory: !meta.lvp_mandatory })}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border w-full text-sm font-semibold transition-all ${
              meta.lvp_mandatory
                ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                : 'border-[var(--border)] text-[var(--text-secondary)]'
            }`}
          >
            {meta.lvp_mandatory ? (
              <ToggleRight size={18} weight="fill" />
            ) : (
              <ToggleLeft size={18} />
            )}
            {meta.lvp_mandatory ? 'SI — Ruta LVP' : 'NO'}
          </button>
        </div>

        <ToggleGroup<AircraftCat>
          label="Cat. max aeronave"
          options={[
            { label: 'B', value: 'B' },
            { label: 'C', value: 'C' },
            { label: 'D', value: 'D' },
            { label: 'F', value: 'F' },
          ]}
          value={meta.cat_max || 'C'}
          onChange={(v) => handleMetaChange({ cat_max: v })}
        />
      </div>
    </div>
  );
};

export default TaxiRouteTool;
